import Phaser from "phaser";
import { PALETTE_NUM } from "../themes/palette.js";
import { DEPTH } from "../objects/depthLayers.js";
import type { PressureProgressState, PressureThreat } from "../scenes/battlefieldPressure.js";
import { resolvePressureThreatIconPosition, shouldRenderPressureThreatIcon } from "./battlePressureBarLayout.js";

const RAIL_WIDTH = 6;
const ICON_SIZE = 14;
const DANGER_PROGRESS = 0.7;
const COLOR_NORMAL = PALETTE_NUM.infoBlue;
const COLOR_WARNING = PALETTE_NUM.accentAmber;
const COLOR_DANGER = PALETTE_NUM.signalDanger;
const COLOR_TRACK = PALETTE_NUM.cellBg;
const DANGER_ICON_MIN_ALPHA = 0.35;
const DANGER_ICON_BLINK_MS = 360;

type PressureBarState = PressureProgressState & {
  readonly threat?: PressureThreat;
};

/**
 * 일반 전투 블록 하강까지 남은 시간을 게임뷰 좌측 세로 rail로 표시한다.
 * 남은 시간 비율은 아래 기준 1→0으로 줄어들고, 전장 위협도에 따라 경고 아이콘과 색을 바꾼다.
 */
export class BattlePressureBar {
  private x: number;
  private y: number;
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly fill: Phaser.GameObjects.Rectangle;
  private readonly icon: Phaser.GameObjects.Graphics;
  private fullHeight: number;
  private lastState?: PressureBarState;
  private iconThreat: PressureThreat = "none";

  constructor(scene: Phaser.Scene, x: number, y: number, height: number) {
    this.x = x;
    this.y = y;
    this.fullHeight = height;
    this.bg = scene.add
      .rectangle(x, y, RAIL_WIDTH, height, COLOR_TRACK, 0.72)
      .setOrigin(0, 0)
      .setDepth(DEPTH.battlePressureBg)
      .setVisible(false)
      .setActive(false);
    this.fill = scene.add
      .rectangle(x, y + height, RAIL_WIDTH, height, COLOR_NORMAL, 1)
      .setOrigin(0, 1)
      .setDepth(DEPTH.battlePressureFill)
      .setVisible(false)
      .setActive(false);
    this.icon = scene.add
      .graphics()
      .setDepth(DEPTH.battlePressureLabel)
      .setVisible(false)
      .setActive(false);
  }

  update(state: PressureBarState): void {
    this.lastState = state;
    const remainingRatio = Phaser.Math.Clamp(
      state.totalMs > 0 ? state.remainingMs / state.totalMs : 0,
      0,
      1,
    );
    this.fill.setSize(RAIL_WIDTH, this.fullHeight * remainingRatio);
    const color = this.resolveFillColor(state);
    this.fill.setFillStyle(color);
    this.updateThreatIcon(state.threat ?? "none", color);
  }

  setLayout(x: number, y: number, height: number): void {
    this.x = x;
    this.y = y;
    this.fullHeight = Math.max(1, height);
    this.bg.setPosition(x, y).setSize(RAIL_WIDTH, this.fullHeight);
    this.fill.setPosition(x, y + this.fullHeight);
    if (this.lastState) this.update(this.lastState);
    else this.fill.setSize(RAIL_WIDTH, this.fullHeight);
  }

  setVisible(visible: boolean): void {
    this.bg.setVisible(visible).setActive(visible);
    this.fill.setVisible(visible).setActive(visible);
    if (!visible) {
      this.stopDangerBlink();
      this.icon.setVisible(false).setActive(false);
      this.iconThreat = "none";
    }
  }

  applyLanguage(): void {
    // no-op: pressure rail has no localized text.
  }

  private resolveFillColor(state: PressureBarState): number {
    if (state.threat === "danger") return COLOR_DANGER;
    if (state.threat === "warning") return COLOR_WARNING;
    return state.progress < DANGER_PROGRESS ? COLOR_NORMAL : COLOR_DANGER;
  }

  private updateThreatIcon(threat: PressureThreat, color: number): void {
    this.icon.clear();
    if (!shouldRenderPressureThreatIcon(threat)) {
      this.stopDangerBlink();
      this.icon.setVisible(false).setActive(false);
      this.iconThreat = "none";
      return;
    }

    const size = ICON_SIZE + 3;
    const { x, y } = resolvePressureThreatIconPosition({
      railX: this.x,
      railY: this.y,
      railWidth: RAIL_WIDTH,
      railHeight: this.fullHeight,
      iconSize: size,
    });
    this.icon
      .setVisible(true)
      .setActive(true)
      .setPosition(x, y)
      .fillStyle(color, 0.98)
      .lineStyle(2, PALETTE_NUM.textBright, 0.95)
      .beginPath()
      .moveTo(0, -size / 2)
      .lineTo(size / 2, size / 2)
      .lineTo(-size / 2, size / 2)
      .closePath()
      .fillPath()
      .strokePath()
      .lineStyle(2, PALETTE_NUM.textBright, 1)
      .beginPath()
      .moveTo(0, -size * 0.18)
      .lineTo(0, size * 0.18)
      .strokePath()
      .fillStyle(PALETTE_NUM.textBright, 1)
      .fillCircle(0, size * 0.34, Math.max(1.4, size * 0.08));
    this.startDangerBlink();
    this.iconThreat = threat;
  }

  private startDangerBlink(): void {
    if (this.iconThreat === "danger") return;
    this.icon.scene.tweens.killTweensOf(this.icon);
    this.icon.setAlpha(1);
    this.icon.scene.tweens.add({
      targets: this.icon,
      alpha: DANGER_ICON_MIN_ALPHA,
      duration: DANGER_ICON_BLINK_MS,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });
  }

  private stopDangerBlink(): void {
    if (this.iconThreat !== "danger") return;
    this.icon.scene.tweens.killTweensOf(this.icon);
    this.icon.setAlpha(1);
  }
}
